/**
 * How did the previous page end? A page iOS / WeChat kills (memory) never gets to
 * say goodbye, and a WeChat page reload looks exactly like a fresh visit. So the
 * live page keeps a small note in localStorage — refreshed every 5 s and at once
 * when it goes to the background — saying what was playing, on which engine
 * (hls.js or the phone's own HLS) and whether it was on screen; a normal close
 * (pagehide) marks it clean. The next start reads the note: not clean → one
 * `page_ended_unexpectedly` event. Nothing else is sent; storage errors are
 * swallowed (private mode, quota) so this can never break the page.
 *
 * Why (2026-10-08): a kids-songs buyer on iPhone/WeChat restarted on 7 of 10 songs.
 * Memory kill with hls.js on iOS 17+, or her switching away? This tells them apart.
 */
import { trackPageEndedUnexpectedly } from './posthogEvents';

export const NOTE_KEY = 'll_page_life';
const BEAT_MS = 5000;
// An older note is a page someone left long ago, not a restart while using the app.
const REPORT_WITHIN_MS = 30 * 60 * 1000;

/** Which engine is playing an element: hls.js, the phone's own HLS, or a plain file (mp3 / mp4). */
export const engineOf = (el) => {
  if (!el) return null;
  if (el.hlsInstance) return 'hlsjs';
  return /\.m3u8(\?|$)/.test(el.currentSrc || el.src || '') ? 'native' : 'file';
};

const navTypeOf = () => {
  try {
    return performance.getEntriesByType('navigation')[0]?.type || null;
  } catch {
    return null;
  }
};

export function createPageLifecycle({
  storage, capture, doc = document, win = window, now = () => Date.now(),
  path = () => win.location?.pathname || null, navType = navTypeOf,
}) {
  let media = null; // { songKey, element }
  let hiddenAt = null;
  let startedAt = null;
  let timer = null;

  const read = () => {
    try {
      const raw = storage.getItem(NOTE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const write = (clean = false) => {
    try {
      const el = media?.element;
      storage.setItem(NOTE_KEY, JSON.stringify({
        at: now(), startedAt, clean, visible: doc.visibilityState === 'visible', hiddenAt, path: path(),
        media: media ? {
          songKey: media.songKey,
          engine: engineOf(el),
          positionS: Math.round(el?.currentTime || 0),
          playing: el ? !el.paused : false,
        } : null,
      }));
    } catch {
      // storage blocked or full: no note, no harm
    }
  };

  const reportPrevious = () => {
    const prev = read();
    if (!prev || prev.clean || !(now() - prev.at <= REPORT_WITHIN_MS)) return;
    const m = prev.media || {};
    capture('page_ended_unexpectedly', {
      how: prev.visible ? 'killed_while_visible' : 'killed_in_background',
      last_alive_s_ago: Math.round((now() - prev.at) / 1000),
      page_lived_s: prev.startedAt ? Math.round((prev.at - prev.startedAt) / 1000) : null,
      hidden_for_s: !prev.visible && prev.hiddenAt ? Math.round((prev.at - prev.hiddenAt) / 1000) : null,
      prev_path: prev.path || null,
      song_key: m.songKey ?? null,
      engine: m.engine ?? null,
      position_s: m.positionS ?? null,
      was_playing: m.playing ?? null,
      nav_type: navType(),
    });
  };

  const onVisibility = () => {
    hiddenAt = doc.visibilityState === 'visible' ? null : now();
    write();
  };
  const onPageHide = () => write(true);

  return {
    start() {
      reportPrevious();
      startedAt = now();
      if (doc.visibilityState !== 'visible') hiddenAt = now();
      write();
      doc.addEventListener('visibilitychange', onVisibility);
      win.addEventListener('pagehide', onPageHide);
      timer = setInterval(() => write(), BEAT_MS);
    },
    /** What's playing now: { songKey, element } (the <video>/<audio>), or null. */
    setMedia(next) {
      media = next;
      write();
    },
    stop() {
      clearInterval(timer);
      doc.removeEventListener('visibilitychange', onVisibility);
      win.removeEventListener('pagehide', onPageHide);
    },
  };
}

// The app's one instance (main.jsx starts it; the song player tells it what's playing).
let appInstance = null;
export function startPageLifecycle() {
  if (appInstance || typeof window === 'undefined') return;
  let storage = null;
  try { storage = window.localStorage; } catch { return; }
  appInstance = createPageLifecycle({ storage, capture: (_event, props) => trackPageEndedUnexpectedly(props) });
  appInstance.start();
}
export function setPageMedia(next) {
  appInstance?.setMedia(next);
}
